// DateTimeRangeRecurrence.js — weekly datetimerange recurrence (client)

// Client copy of weekly datetimerange recurrence helpers (mirrors functions/utils/events).

function normalizeTimeZone(value) {
  const timeZone = String(value || '').trim();
  if (!timeZone) return 'UTC';
  try {
    Intl.DateTimeFormat(undefined, { timeZone }).format(new Date());
    return timeZone;
  } catch (_error) {
    return 'UTC';
  }
}

function readPart(parts, type) {
  const match = parts.find((part) => part.type === type);
  return Number(match?.value || 0);
}

function getZonedDateTimeParts(date, timeZone) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const asUtc = Date.UTC(
    readPart(parts, 'year'),
    readPart(parts, 'month') - 1,
    readPart(parts, 'day'),
    readPart(parts, 'hour'),
    readPart(parts, 'minute'),
    readPart(parts, 'second'),
  );
  return asUtc - date.getTime();
}

function zonedDateTimeToUtcMs({
  year, month, day, hour = 0, minute = 0, second = 0, millisecond = 0,
}, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  return utcGuess - getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
}

function addDaysInTimeZone(date, dayCount, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const noonUtc = zonedDateTimeToUtcMs({
    year: readPart(parts, 'year'),
    month: readPart(parts, 'month'),
    day: readPart(parts, 'day') + dayCount,
    hour: 12,
  }, timeZone);
  return new Date(noonUtc);
}

function getDayOfWeekInTimeZone(date, timeZone) {
  const day = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(date);
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[day] ?? 0;
}

const MAX_RECURRENCE_OCCURRENCES = 120;

const BYDAY_ORDER = Object.freeze(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']);
const BYDAY_TO_INDEX = Object.freeze({
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
});
const INDEX_TO_BYDAY = Object.freeze(['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']);
const BYDAY_LABELS = Object.freeze({
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
});

function cleanString(value) {
  return String(value || '').trim();
}

function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value?.toMillis === 'function') {
    try {
      const ms = value.toMillis();
      return Number.isFinite(ms) ? Math.round(ms) : null;
    } catch (_error) {
      return null;
    }
  }
  if (typeof value?.seconds === 'number') {
    const nanos = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
    return Math.round(value.seconds * 1000 + nanos / 1e6);
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function getCivilDateKey(ms, timeZone = 'UTC') {
  const safeMs = toMillis(ms);
  if (!Number.isFinite(safeMs)) return '';
  const safeTimeZone = normalizeTimeZone(timeZone);
  const parts = getZonedDateTimeParts(new Date(safeMs), safeTimeZone);
  const year = String(readPart(parts, 'year')).padStart(4, '0');
  const month = String(readPart(parts, 'month')).padStart(2, '0');
  const day = String(readPart(parts, 'day')).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeExDate(value) {
  const raw = cleanString(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const ms = toMillis(value);
  if (!Number.isFinite(ms)) return '';
  return getCivilDateKey(ms, 'UTC');
}

/**
 * Coerce / validate recurrence object. Returns null when absent or empty.
 * Returns { ok:false, error } when present but invalid.
 */
function normalizeRecurrence(raw) {
  if (raw == null) return { ok: true, recurrence: null };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, error: 'Recurrence must be an object.', recurrence: null };
  }

  const freq = cleanString(raw.freq).toUpperCase();
  if (!freq) return { ok: true, recurrence: null };
  if (freq !== 'WEEKLY') {
    return { ok: false, error: 'Only weekly recurrence is supported.', recurrence: null };
  }

  const interval = raw.interval == null ? 1 : Number(raw.interval);
  if (!Number.isInteger(interval) || interval !== 1) {
    return { ok: false, error: 'Weekly recurrence must repeat every week.', recurrence: null };
  }

  const byDayRaw = Array.isArray(raw.byDay) ? raw.byDay : [];
  const byDay = [];
  const seen = new Set();
  byDayRaw.forEach((token) => {
    const day = cleanString(token).toUpperCase();
    if (!BYDAY_TO_INDEX.hasOwnProperty(day) || seen.has(day)) return;
    seen.add(day);
    byDay.push(day);
  });
  byDay.sort((a, b) => BYDAY_ORDER.indexOf(a) - BYDAY_ORDER.indexOf(b));
  if (!byDay.length) {
    return { ok: false, error: 'Pick at least one weekday.', recurrence: null };
  }

  const untilMs = toMillis(raw.until);
  const countRaw = raw.count == null || raw.count === '' ? null : Number(raw.count);
  const hasUntil = Number.isFinite(untilMs);
  const hasCount = Number.isInteger(countRaw) && countRaw > 0;

  if (hasUntil === hasCount) {
    return {
      ok: false,
      error: 'Set either an end date or an occurrence count.',
      recurrence: null,
    };
  }

  if (hasCount && countRaw > MAX_RECURRENCE_OCCURRENCES) {
    return {
      ok: false,
      error: `Occurrence count cannot exceed ${MAX_RECURRENCE_OCCURRENCES}.`,
      recurrence: null,
    };
  }

  const exDates = [];
  const exSeen = new Set();
  (Array.isArray(raw.exDates) ? raw.exDates : []).forEach((entry) => {
    const key = normalizeExDate(entry);
    if (!key || exSeen.has(key)) return;
    exSeen.add(key);
    exDates.push(key);
  });
  exDates.sort();

  return {
    ok: true,
    recurrence: {
      freq: 'WEEKLY',
      byDay,
      interval: 1,
      until: hasUntil ? untilMs : null,
      count: hasCount ? countRaw : null,
      exDates,
    },
  };
}

function expandWeeklyOccurrences({
  startMs,
  endMs,
  recurrence,
  timeZone = 'UTC',
} = {}) {
  const normalized = normalizeRecurrence(recurrence);
  if (!normalized.ok || !normalized.recurrence) {
    return { ok: normalized.ok, error: normalized.error || null, occurrences: [] };
  }

  const safeStartMs = toMillis(startMs);
  const safeEndMs = toMillis(endMs);
  if (!Number.isFinite(safeStartMs)) {
    return { ok: false, error: 'Start date is required.', occurrences: [] };
  }

  const durationMs = Number.isFinite(safeEndMs) && safeEndMs >= safeStartMs
    ? safeEndMs - safeStartMs
    : 60 * 60 * 1000;

  const rule = normalized.recurrence;
  const safeTimeZone = normalizeTimeZone(timeZone);
  const byDayIndexes = new Set(rule.byDay.map((token) => BYDAY_TO_INDEX[token]));
  const exDates = new Set(rule.exDates);
  const startParts = getZonedDateTimeParts(new Date(safeStartMs), safeTimeZone);
  const startHour = readPart(startParts, 'hour');
  const startMinute = readPart(startParts, 'minute');
  const startSecond = readPart(startParts, 'second');

  const untilEndMs = Number.isFinite(rule.until)
    ? rule.until
    : null;
  const maxCount = Number.isInteger(rule.count) ? rule.count : MAX_RECURRENCE_OCCURRENCES;

  const occurrences = [];
  let cursor = new Date(safeStartMs);
  const startDateKey = getCivilDateKey(safeStartMs, safeTimeZone);
  // Safety: never walk more than ~2 years of days.
  const maxDayWalk = Math.max(maxCount * 7 + 14, 370 * 2);

  for (let dayIndex = 0; dayIndex < maxDayWalk; dayIndex += 1) {
    if (occurrences.length >= maxCount) break;

    const cursorMs = cursor.getTime();
    const dateKey = getCivilDateKey(cursorMs, safeTimeZone);
    if (dateKey < startDateKey) {
      cursor = addDaysInTimeZone(cursor, 1, safeTimeZone);
      continue;
    }

    const weekday = getDayOfWeekInTimeZone(cursor, safeTimeZone);
    if (!byDayIndexes.has(weekday) || exDates.has(dateKey)) {
      cursor = addDaysInTimeZone(cursor, 1, safeTimeZone);
      continue;
    }

    const parts = getZonedDateTimeParts(cursor, safeTimeZone);
    const occurrenceStartMs = zonedDateTimeToUtcMs({
      year: readPart(parts, 'year'),
      month: readPart(parts, 'month'),
      day: readPart(parts, 'day'),
      hour: startHour,
      minute: startMinute,
      second: startSecond,
      millisecond: 0,
    }, safeTimeZone);

    if (Number.isFinite(untilEndMs) && occurrenceStartMs > untilEndMs) break;

    occurrences.push({
      occurrenceDateKey: dateKey,
      startMs: occurrenceStartMs,
      endMs: occurrenceStartMs + durationMs,
    });

    if (occurrences.length > MAX_RECURRENCE_OCCURRENCES) {
      return {
        ok: false,
        error: `Series would create more than ${MAX_RECURRENCE_OCCURRENCES} occurrences. Shorten the end date or count.`,
        occurrences: [],
      };
    }

    cursor = addDaysInTimeZone(cursor, 1, safeTimeZone);
  }

  if (!occurrences.length) {
    return { ok: false, error: 'No occurrences match this recurrence.', occurrences: [] };
  }

  return { ok: true, error: null, occurrences };
}

function addExDateToRecurrence(recurrence, dateKey) {
  const normalized = normalizeRecurrence(recurrence);
  if (!normalized.ok || !normalized.recurrence) return null;
  const key = normalizeExDate(dateKey);
  if (!key) return normalized.recurrence;
  const exDates = Array.from(new Set([...(normalized.recurrence.exDates || []), key])).sort();
  return {
    ...normalized.recurrence,
    exDates,
  };
}

function formatRecurrenceHint(recurrence) {
  const normalized = normalizeRecurrence(recurrence);
  if (!normalized.ok || !normalized.recurrence) return '';
  const days = normalized.recurrence.byDay.map((token) => BYDAY_LABELS[token] || token).join(', ');
  if (Number.isFinite(normalized.recurrence.until)) {
    const until = new Date(normalized.recurrence.until);
    const untilLabel = until.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${days} · until ${untilLabel}`;
  }
  if (normalized.recurrence.count) {
    return `${days} · ${normalized.recurrence.count} times`;
  }
  return days;
}

export {
  BYDAY_LABELS,
  BYDAY_ORDER,
  BYDAY_TO_INDEX,
  INDEX_TO_BYDAY,
  MAX_RECURRENCE_OCCURRENCES,
  addExDateToRecurrence,
  expandWeeklyOccurrences,
  formatRecurrenceHint,
  getCivilDateKey,
  normalizeExDate,
  normalizeRecurrence,
  toMillis,
};
